import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Toasts } from '@deseretbook/react-ui'
import { DeseretBook } from '../../dbcom/api'
import { Logger, formatErrorMessage } from '../../dbcom'

const logger = new Logger({ context: 'AddressFormManager' })

/**
 * Manages country/state data in a Form. Generally to be used with a set of
 * fields like the `AddressFormFields`. If you use this with the `AddressFormFields`
 * component, then you can just pass all the extra params provided by this render prop
 * to the `AddressFormFields`, eg:
 *
 * ```
 * <AddressFormManager address={this.state.address}>
 *   {(addressFormProps) => (
 *     <AddressFormFields {...addressFormProps} />
 *   )}
 * </AddressFormManager>
 * ```
 */
class AddressFormManager extends Component {
  /**
   * AddressFormManager constructor.
   */
  constructor() {
    super()

    // We'll cache the list of countries here.
    this.countries = []
    // We'll cache each country's states data here as it is loaded.
    this.countryStates = {}

    this.state = {
      // We'll set this to true any time we have to get data from the server.
      isFetchingData: false,
      // True once initial country/state data is loaded.
      isInitialized: false,
    }

    this.fetchCountries = this.fetchCountries.bind(this)
    this.fetchStates = this.fetchStates.bind(this)
    this.getDefaultCountry = this.getDefaultCountry.bind(this)
    this.getDefaultState = this.getDefaultState.bind(this)
    this.getSelectedCountry = this.getSelectedCountry.bind(this)
    this.getSelectedState = this.getSelectedState.bind(this)
    this.onInitialized = this.onInitialized.bind(this)
    this.onChangeHandler = this.onChangeHandler.bind(this)
  }

  /**
   * When the component mounts we need to initialize country/state data.
   */
  componentDidMount() {
    this.fetchCountries()
  }

  /**
   * When the component updates, we need to compare the current and previous
   * address to see if we need to fetch new state information.
   */
  componentDidUpdate({ address: previousAddress }, { isInitialized }) {
    const { address } = this.props

    // If the country has changed we want to get new states for the new country.
    if (
      previousAddress.countryId &&
      address.countryId !== previousAddress.countryId
    ) {
      this.fetchStates()
    }

    // Do some stuff if the form is just barely initialized.
    if (!isInitialized && this.state.isInitialized) {
      this.onInitialized()
    }
  }

  /**
   * Retrieves country information from the server.
   */
  fetchCountries() {
    try {
      this.setState({ isFetchingData: true }, async () => {
        const response = await DeseretBook.country().all({ perPage: 1000 })

        this.countries = response.countries

        // Now that we know stuff about the countries we can try to get state data.
        this.fetchStates()
      })
    } catch ({ message, errors }) {
      logger.error(
        `Unable to fetch countries: "${formatErrorMessage(message, errors)}"`,
        { errors },
      )
      Toasts.danger(
        'Unable to get countries',
        formatErrorMessage(message, errors),
      )
      this.setState(() => ({ isFetchingData: false }))
    }
  }

  /**
   * Retrieves states information for the selected or default country if one
   * isn't currently selected.
   *
   * Caches results by country so if states information is requested for a country
   * multiple times, then only one request will actually be made.
   */
  fetchStates() {
    const country = this.getSelectedCountry() || this.getDefaultCountry()
    const isCached = country && country.id && !!this.countryStates[country.id]

    try {
      this.setState({ isFetchingData: true }, async () => {
        if (country && !isCached) {
          this.countryStates[country.id] = await DeseretBook.country()
            .state()
            .all({ country })
        }

        this.setState({ isFetchingData: false, isInitialized: true })
      })
    } catch ({ message, errors }) {
      logger.error(
        `Unable to fetch states/provinces: "${formatErrorMessage(
          message,
          errors,
        )}"`,
        { errors, country },
      )
      Toasts.danger(
        'Unable to get states or provinces',
        formatErrorMessage(message, errors),
      )
      this.setState({ isFetchingData: false, isInitialized: true })
    }
  }

  /**
   * Returns the default country information based on the `defaultCountryIso` prop.
   *
   * @returns {object|undefined} - Returns the default country object if available.
   */
  getDefaultCountry() {
    return this.countries.filter(
      ({ iso }) => iso === this.props.defaultCountryIso,
    )[0]
  }

  /**
   * Returns the default state information based on the `defaultStateAbbreviation`
   * prop. If there isn't a state with the specified abbreviation as part of the
   * default country, then undefined is returned.
   *
   * @returns {object|undefined} - Returns the default country object if available.
   *
   */
  getDefaultState() {
    const defaultCountry = this.getDefaultCountry() || {}
    const { states = [] } = this.countryStates[defaultCountry.id] || {}

    return states.filter(
      ({ abbr }) => abbr === this.props.defaultStateAbbreviation,
    )[0]
  }

  /**
   * Returns the selected country information based on the `countryId` in the `address`.
   * If there isn't a matching country then `undefined` is returned.
   *
   * @returns {object|undefined} - Returns the selected country object.
   */
  getSelectedCountry() {
    const {
      address: { countryId },
    } = this.props

    return this.countries.filter(({ id }) => id == countryId)[0]
  }

  /**
   * Returns the selected states information based on the `countryId` and `stateId`
   * in the `address` prop. If there isn't a matching state in the coutnry then
   * `undefined` is returned.
   *
   * @returns {object|undefined} - Returns the selected country object.
   */
  getSelectedState() {
    const {
      address: { countryId, stateId },
    } = this.props
    const { states = [] } = this.countryStates[countryId] || {}

    return states.filter(({ id }) => id === stateId)[0]
  }

  /**
   * A custom lifecycle method that should be run once the form manager has
   * been initialized. Determines if the default country/state should become
   * the selected country/state.
   */
  onInitialized() {
    const { address, onChange } = this.props

    if (!address.countryId) {
      const country = this.getDefaultCountry() || {}
      const state = this.getDefaultState() || {}

      onChange({ countryId: country.id, stateId: state.id })
    }
  }

  /**
   * Wrapper for the onChange event handler. This takes in the event, parses
   * the field changed, and then determines what changes should be made to the
   * actual address. Returns suggested changes through the actual `onChange` prop.
   *
   * Some custom changes that are suggested are:
   * - If the `countryId` changes, then `stateId` and `stateName` are reset as well.
   *
   * @param {event} e - change event
   */
  onChangeHandler({ target }) {
    const { name, value, type, checked } = target
    const { onChange } = this.props

    const changes = {
      [name]: type === 'checkbox' ? checked : value,
    }

    // Reset state id/name if the country is the one that changes.
    if (name === 'countryId') {
      changes.stateId = ''
      changes.stateName = ''
    }

    onChange(changes)
  }

  /**
   * Gathers together useful information and passes it down to the children.
   */
  render() {
    const { children, address } = this.props
    const { isFetchingData, isInitialized } = this.state

    const selectedCountry = this.getSelectedCountry()
    const selectedState = this.getSelectedState()
    const defaultCountry = this.getDefaultCountry()
    const defaultState = this.getDefaultState()
    const country = selectedCountry || defaultCountry
    const stateData =
      country && country.id ? this.countryStates[country.id] || {} : {}

    return children({
      address,
      selectedCountry,
      selectedState,
      defaultCountry,
      defaultState,
      isFetchingData,
      isInitialized,
      countries: this.countries,
      states: stateData.states || [],
      statesRequired: !!stateData.statesRequired,
      onChange: this.onChangeHandler,
    })
  }
}

AddressFormManager.propTypes = {
  children: PropTypes.func.isRequired,
  // Current address data
  address: PropTypes.object,
  // ISO of default country to select.
  defaultCountryIso: PropTypes.string,
  // Abbr of default state to select in default country.
  defaultStateAbbreviation: PropTypes.string,
  // Callback for when input value changes.
  onChange: PropTypes.func,
}

AddressFormManager.defaultProps = {
  address: {},
  defaultCountryIso: 'US',
  defaultStateAbbreviation: 'UT',
  onChange: () => {},
}

export default AddressFormManager
